import { AxiosError } from 'axios';
import { dateUtils } from './dateUtils';
import { StringNumerNull } from './types/common';
import { CustomerNameListData } from './types/dairy';
import { CustomerNameListData as HistoryByModelCustomerNameListData, CustomerNumberListData as HistoryByModelCustomerNumberListData } from './types/HistoryByModel';

// Function to retrieve cookie value by name
export const getCookie = (name: string): string => {
  if (typeof window === 'undefined') {
    return '';
  }
  const cookieString = document.cookie;
  // const cookies = cookieString.split(';').map((cookie) => cookie.trim())
  const cookies = cookieString.split(';').map(cookie => cookie.trim());
  for (const cookie of cookies) {
    const [cookieName, cookieValue] = cookie.split('=');
    if (cookieName === name) {
      return decodeURIComponent(cookieValue);
    }
  }
  return ''; // Return null if cookie not found
};
export const formatPrice = (price: StringNumerNull) => {
  if (price === null || price === undefined || price === '') {
    return '';
  }
  const numberPrice = parseFloat(String(price));
  if (isNaN(numberPrice)) {
    return '';
  }
  return `$${numberPrice.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};
export const formatPriceWithoutSymbol = (price: StringNumerNull) => {
  if (price === null || price === undefined || price === '') {
    return '';
  }
  const formattedPrice = parseFloat(String(price)).toFixed(2);
  return `${formattedPrice}`;
};
export const formatCurrency = (value: string | number): string => {
  if (value === 0 || value === '0') {
    return '$0.00';
  }
  if ((!value || value === '') && isNaN(Number(value))) {
    return '';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2
  }).format(Number(value));
};
export const formatCurrency2 = (value: string | number): string => {
  if (value === 0 || value === '0') {
    return '$0';
  }
  if ((!value || value === '') && isNaN(Number(value))) {
    return '';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Number(value));
};
export const formatToK = (value?: number): string => {
  if (!value || isNaN(Number(value))) return '';
  return `${Math.floor(Number(value) / 1000)}`;
};
export function removeCurrencyFormatting(formattedValue: string) {
  return formattedValue.replace(/[$,]/g, '');
}

// Format number with 2 decimal places and comma separation (without $ sign)
export const formatNumberWithCommas = (value: StringNumerNull): string => {
  if (!value && value !== 0) return '';

  // Remove any non-numeric characters except decimal point
  const numericValue = String(value).replace(/[^0-9.-]/g, '');
  const number = parseFloat(numericValue);
  if (isNaN(number)) return '';

  // Format with 2 decimal places and comma separation
  return number.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Handle currency/number input change - returns clean numeric value
export const handleCurrencyInputChange = (inputValue: string): string => {
  // Allow empty input
  if (!inputValue) return '';

  // Remove commas and $ sign
  const cleanedValue = inputValue.replace(/[$,]/g, '');

  // Prevent ReDoS with length check before regex
  if (cleanedValue.length > 50) return '';

  // Handle empty string explicitly (SonarQube fix)
  if (cleanedValue === '') {
    return cleanedValue;
  }

  // Use a completely different approach - no nested quantifiers at all
  // This regex uses atomic groups that prevent backtracking
  if (/^-?(\d+(\.\d*)?|\.\d+)$/.test(cleanedValue)) {
    return cleanedValue;
  }

  // Return previous valid value if invalid input
  return '';
};

// Format value on blur to ensure 2 decimal places
export const formatOnBlur = (value: string | number): string => {
  if (!value && value !== 0) return '';
  const number = parseFloat(String(value));
  if (!isNaN(number)) {
    // Return numeric value with 2 decimal places
    return number.toFixed(2);
  }
  return '';
};
export const extractAssetId = (title: string) => {
  const regex = /Asset #(\d+)/;
  const match = regex.exec(title);
  return match ? match[1] : null;
};
export const extractVendorId = (title: string) => {
  const regex = /Vendor #(.+)/;
  const match = regex.exec(title);
  return match ? match[1] : null;
};
export const extractReserveUnreserveAssetId = (title: string) => {
  const regex = /Reserve\/Unreserve Assets Model #(.+)/;
  const match = regex.exec(title);
  return match ? match[1] : null;
};
export const extractBlobId = (title: string) => {
  const regex = /Blob Content #(.+)/;
  const match = regex.exec(title);
  return match ? match[1] : null;
};
export const extractManufacturerId = (title: string) => {
  const regex = /#([^,#]+)/;
  const match = regex.exec(title);
  return match ? match[1].trim() : null;
};
export const extractManufacturerIdForMfgModel = (title: string) => {
  const regex = /Manufacturer - ([^\n,]+)/;
  const match = regex.exec(title);
  return match ? match[1] : null;
};
const patterns = {
  asset: /Asset #(\d+)/,
  vendor: /Vendor #(.+)/,
  reserveUnreserve: /Reserve\/Unreserve Assets Model #(.+)/,
  blob: /Blob Content #(.+)/,
  manufacturer: /#([^,#]+)/,
  manufacturerModel: /Manufacturer - ([^\n,]+)/,
  poDetailPoId: /PO Details PR #(\d+), Line #\d+/,
  poDetailLineNum: /PO Details PR #\d+, Line #(\d+)/,
  rta: /RTA #(.+)/,
  dairy: /Dairy #(.+)/,
  poView: /Purchase Order #([A-Za-z0-9]+)/,
  freight_seq: /Seq #([A-Za-z0-9.]+)/,
  freight_carrier: /Carrier #([A-Za-z0-9]+)/,
  freight_airport: /Airport #([A-Za-z0-9]+)/,
  freight_boxNumber: /boxNumber #(\d+(?:\.\d+)?)/,
  purchaseRequest: /Purchase Request #([A-Za-z0-9]+)/,
  changePoLineDataPR: /PR #([^\n,]+)/,
  changePoLineDataLine: /Line #([^\n,]+)/,
  changePoLineDataPO: /POID #(.+)/,
  inspectionPO: /PO\s+#(\d+[A-Z]?)/,
  inspectionPOL: /POL\s+#(\d+)/,
  inspectionPR: /PR\s+#(\d+)/,
  packingRa: /Packing #(\d+)/,
  packingCo: /Packing #\d+\s+#(\d+)/,
  accessories: /Accessories #(.+)/,
  freightCarrier: /Freight Invoice #([^\s#]+)/,
  freightAir: /Freight Invoice #[^\s#]+ #([^\s#]+)/,
  freightSeq: /Freight Invoice #[^\s#]+ #[^\s#]+ #([^\s#]+)/,
  poCreate: /Create Purchase Order #(\d+)/,
  calCardAssetId: /Asset #([A-Za-z0-9]+)/,
  changeShipToRA: /RA #(\d+)/,
  changeShipToCmp: /Cmp #(\d+)/,
  calCardEmpId: /Emp #([A-Za-z0-9]+)/,
  calCardStartDate: /Start #(\d{4}-\d{2}-\d{2})/,
  calCardEndDate: /End #(\d{4}-\d{2}-\d{2})/,
  accessoryPhotoListAssetId: /AssetId #([A-Za-z0-9]+)/,
  accessoryPhotoListMfgId: /MfgId #([A-Za-z0-9]+)/,
  accessoryPhotoListRaNum: /RaNum #([A-Za-z0-9]+)/,
  accessoryPhotoListCompNum: /CompNum #([A-Za-z0-9]+)/,
  specialInstructionId: /Special Instruction PR #(\d+), Line #\d+/,
  specialInstructionLineNum: /Special Instruction PR #\d+, Line #(\d+)/,
  customerAddress: /Customer Address #(\d+)/,
  customerAddressAdd: /address #([A-Za-z0-9]+)/,
  customerAddressActive: /active #([A-Za-z0-9]+)/,
  customerAddressNew: /new #([A-Za-z0-9]+)/,
  customerAddressUpdate: /update #([A-Za-z0-9]+)/,
  customerAddressGeo: /geo #([^,]+?)(?:,|$)/,
  customerAddressCounty: /county #(.*?)(?=,\s*(?:state|city|zip|country)\s*#|$)/,
  customerAddressState: /state #(.*?)(?=,\s*(?:city|zip|country)\s*#|$)/,
  customerAddressCity: /city #(.*?)(?=,\s*(?:zip|country)\s*#|$)/,
  customerAddressZip: /zip #(.*?)(?=,\s*country\s*#|$)/,
  customerAddressCountry: /country #(.+?)(?:,|$)/,
  rentalAgreement: /Rental Agreement #(\d+)/,
  customerId: /customerId #([A-Za-z0-9]+)/
};
export const extractId = (title: string, type: keyof typeof patterns): string | null => {
  const pattern = patterns[type];
  const match = pattern.exec(title);
  return match ? match[1].trim() : null;
};

// Helper function to handle the download of base64 data
export const downloadFile = (base64Data: string, contentType: string, fileName: string) => {
  // Decode base64 to binary
  const byteCharacters = atob(base64Data); // Decode the base64 string
  // const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
  const byteNumbers = Array.from(byteCharacters, char => char.charCodeAt(0));
  const byteArray = new Uint8Array(byteNumbers);

  // Create a Blob from the binary data
  const blob = new Blob([byteArray], {
    type: contentType
  });

  // Create a link element to trigger download
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();

  // Clean up by removing the link element
  document.body.removeChild(link);
};
export
// Helper function to check if the error is an AxiosError
function isAxiosError(error: unknown): error is AxiosError {
  return (error as AxiosError).isAxiosError !== undefined;
}
export const getNoOptionsMessage = (isLoading: boolean, customMessage = 'No results found') => () => isLoading ? 'Loading...' : customMessage;
type StickyOptions = {
  zIndexStart?: number;
  headerBg?: string;
  bodyBg?: string;
};

// Helper function to get header styles based on variant
function getHeaderStyles(variant?: 'light' | 'dark' | 'warning') {
  const headerStyles = {
    light: {
      background: '#e7e7e7',
      color: '#333'
    },
    dark: {
      background: 'var(--primary)',
      color: '#fff'
    },
    warning: {
      background: '#ffedcc',
      color: '#664400'
    }
  };
  const style = headerStyles[variant ?? 'light'];
  return {
    headerBg: style.background,
    headerColor: style.color
  };
}

// Helper function to calculate column offsets
function calculateColumnOffsets(table: HTMLTableElement, stickyCount: number, columnWidths?: Record<string, number>, columns?: Array<{
  key?: string;
  accessor?: string | number | symbol;
  width?: number;
}>): number[] {
  const headerCells = table.querySelectorAll(`thead tr th`);
  const offsets: number[] = [];
  let totalLeft = 0;
  for (let i = 0; i < stickyCount; i++) {
    const th = headerCells[i] as HTMLElement;
    if (!th) continue;
    let columnWidth: number;
    if (columnWidths && columns && i < columns.length) {
      const column = columns[i];
      const colKey = column.key || column.accessor?.toString() || `col-${i}`;
      columnWidth = columnWidths[colKey] || column.width || th.offsetWidth;
    } else {
      columnWidth = th.offsetWidth;
    }
    offsets.push(totalLeft);
    totalLeft += columnWidth;
  }
  return offsets;
}

// Helper function to check if a row is selected
function isRowSelected(element: HTMLElement, selectedRowIndex?: number | number[]): boolean {
  if (selectedRowIndex === undefined) return false;
  const row = element.closest('tr');
  if (!row) return false;
  const rowIndex = Array.from(row.parentElement?.children || []).indexOf(row);
  return Array.isArray(selectedRowIndex) ? selectedRowIndex.includes(rowIndex) : selectedRowIndex === rowIndex;
}

// Helper function to apply header cell styling
function applyHeaderCellStyling(element: HTMLElement, headerBg: string, headerColor: string): void {
  element.style.background = headerBg;
  element.style.color = headerColor;
}

// Helper function to apply selected row cell styling
function applySelectedRowCellStyling(element: HTMLElement): void {
  element.style.background = 'rgb(96 165 250)';
  element.style.color = 'white';
}

// Helper function to apply normal body cell styling
function applyNormalBodyCellStyling(element: HTMLElement, bodyBg: string, bodyColor: string): void {
  element.style.background = bodyBg;
  element.style.color = bodyColor;
}

// Helper function to apply border styling
function applyBorderStyling(element: HTMLElement, index: number): void {
  element.style.borderSpacing = '0px';
  element.style.border = 'hidden';
  element.style.borderBottom = '1px solid #c1c4de';
  element.style.borderTop = 'hidden';
  element.style.margin = '0px';
  if (index === 0) {
    element.style.boxShadow = 'inset 1px -0px 0px var(--outlinecolor), inset -1px 1px 0px var(--outlinecolor), inset 1px -0px 0px var(--outlinecolor)';
  } else {
    element.style.boxShadow = ' inset -1px 1px 0px var(--outlinecolor)';
  }
}

// Helper function to setup intersection observer for last row
function setupLastRowObserver(table: HTMLTableElement): void {
  const rows = table.querySelectorAll('tbody tr');
  if (rows.length === 0) return;
  const lastRow = rows[rows.length - 1];
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const lastRowCells = lastRow.querySelectorAll('[data-sticky-index]');
        lastRowCells.forEach((cell, index) => {
          const el = cell as HTMLElement;
          if (index === 0) {
            el.style.boxShadow = 'inset 1px -0px 0px var(--outlinecolor), inset -1px 1px 0px var(--outlinecolor), inset 1px -0px 0px var(--outlinecolor),inset 0px -1px 0px var(--outlinecolor)';
          } else {
            el.style.boxShadow = 'inset -1px 1px 0px var(--outlinecolor), inset 0px -1px 0px var(--outlinecolor)';
          }
        });
      }
    });
  }, {
    root: table.parentElement,
    threshold: 1.0
  });
  observer.observe(lastRow);
}
export function applyStickyColumns(table: HTMLTableElement, stickyCount: number = 1, variant?: 'light' | 'dark' | 'warning', options: StickyOptions = {}, columnWidths?: Record<string, number>, columns?: Array<{
  key?: string;
  accessor?: string | number | symbol;
  width?: number;
}>, selectedRowIndex?: number | number[], rowBackgroundColors // New parameter
?: string[]) {
  if (!table) return;
  const {
    headerBg,
    headerColor
  } = getHeaderStyles(variant);
  const bodyBg = options.bodyBg ?? '#fff';
  const bodyColor = '#000';
  const offsets = calculateColumnOffsets(table, stickyCount, columnWidths, columns);

  // First, ensure caption has proper z-index
  const caption = table.querySelector('caption');
  if (caption) {
    const captionEl = caption as HTMLElement;
    captionEl.style.zIndex = '30';
  }
  const stickyCells = table.querySelectorAll('[data-sticky-index]');
  const getRowIndex = (el: HTMLElement): number => {
    const trElement = el.closest('tr');
    if (!trElement?.parentElement) return -1;
    return Array.from(trElement.parentElement.children).indexOf(trElement);
  };
  const handleHeaderCell = (el: HTMLElement, index: number) => {
    el.style.zIndex = `${20 + index}`;
    applyHeaderCellStyling(el, headerBg, headerColor);
  };
  const handleBodyCell = (el: HTMLElement, index: number, isSelectedRow: boolean) => {
    el.style.zIndex = `${10 + index}`;
    if (isSelectedRow) {
      applySelectedRowCellStyling(el);
      return;
    }
    const rowIndex = getRowIndex(el);
    const customRowBgColor = rowBackgroundColors && rowIndex !== -1 ? rowBackgroundColors[rowIndex] : null;
    applyNormalBodyCellStyling(el, customRowBgColor ?? bodyBg, bodyColor);
  };
  stickyCells.forEach(cell => {
    const index = parseInt(cell.getAttribute('data-sticky-index') ?? '-1');
    if (index < 0 || index >= stickyCount) return;
    const el = cell as HTMLElement;
    el.style.position = 'sticky';
    el.style.left = `${index > 0 ? offsets[index] - 1 * index : offsets[index]}px`;
    const isHeader = el.tagName.toLowerCase() === 'th';
    const isSelectedRow = !isHeader && isRowSelected(el, selectedRowIndex);
    if (isHeader) {
      handleHeaderCell(el, index);
    } else {
      handleBodyCell(el, index, isSelectedRow);
    }
    applyBorderStyling(el, index);
  });
  setupLastRowObserver(table);
}
export const formatDate = (isoString: string) => {
  if (!isoString) return '';
  const date = dateUtils.parse.safe(isoString);
  if (!date) return '';
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  const hours = `${date.getHours()}`.padStart(2, '0');
  const minutes = `${date.getMinutes()}`.padStart(2, '0');
  const seconds = `${date.getSeconds()}`.padStart(2, '0');

  // Constructing the custom format: YYYY-MM-DD, HH:MMSS
  return `${year}-${month}-${day}, ${hours}:${minutes}:${seconds}`;
};
export const formatDateComment = (isoString: string) => {
  const date = dateUtils.parse.safe(isoString);
  if (!date) return '';
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  const hours = `${date.getHours()}`.padStart(2, '0');
  const minutes = `${date.getMinutes()}`.padStart(2, '0');

  // Constructing the custom format: YYYY-MM-DD, HH:MMSS
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};
interface LoaderAnimateIconProps {
  height?: number;
  width?: number;
}
export const LoaderAnimateIcon = ({
  height,
  width
}: LoaderAnimateIconProps) => <div className="flex justify-center" data-sentry-component="LoaderAnimateIcon" data-sentry-source-file="helper.tsx">
    <div className={`h-${height ?? 6} w-${width ?? 6} animate-spin rounded-full border-4 border-blue-500 border-t-transparent`}></div>
  </div>;
export function OpenBlobURLPdf(pdfString: string) {
  if (pdfString === null || pdfString === undefined || pdfString === '') {
    console.error('File not found');
    return;
  }
  // Convert the base64 string to a binary string
  const byteCharacters = atob(pdfString);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  // Create a Uint8Array from the byte numbers
  const byteArray = new Uint8Array(byteNumbers);

  // Create a blob from the array
  const blob = new Blob([byteArray], {
    type: 'application/pdf'
  });

  // Create a URL for the blob and open it in a new tab
  const blobUrl = URL.createObjectURL(blob);
  window.open(blobUrl, '_blank');
}
export function OpenPdfFromBlob(blob: Blob) {
  if (!blob) {
    console.error('No blob provided');
    return;
  }

  // Create a URL for the blob and open it in a new tab
  const blobUrl = URL.createObjectURL(blob);
  window.open(blobUrl, '_blank');

  // Clean up the URL after a delay to avoid memory leaks
  setTimeout(() => {
    URL.revokeObjectURL(blobUrl);
  }, 100);
}
export function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  if (parts.length < 2) {
    console.error('Invalid filename: no extension found');
    return '';
  }
  return parts.pop()!.toLowerCase();
}
const mimeTypeMap: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
};
export function openBlobInNewTab(base64String: string, fileExtension: string) {
  if (!base64String || !fileExtension) {
    console.error('Invalid input: base64String or fileExtension is missing.');
    return;
  }
  const mimeType = mimeTypeMap[fileExtension.toLowerCase()];
  if (!mimeType) {
    console.error(`Unsupported file type: ${fileExtension}`);
    return;
  }
  try {
    const byteCharacters = atob(base64String);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {
      type: mimeType
    });
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
  } catch (error) {
    console.error('Error opening file:', error);
  }
}
// Helper: Extract first error message from Formik errors
export function getFirstErrorMsg(errors: Record<string, unknown>): string {
  const errorKey = Object.keys(errors)[0];
  const errorMsg = errors[errorKey];
  return typeof errorMsg === 'string' ? errorMsg : 'Invalid input';
}

// Helper: Transform and filter values for submit
export function getFilteredParams(values: Record<string, unknown>): Record<string, string | number | boolean> {
  const transformed = Object.fromEntries(Object.entries(values).map(([key, value]) => {
    if (typeof value === 'object' && value !== null && 'value' in value) {
      return [key, value];
    }
    return [key, value];
  }));
  return Object.fromEntries(Object.entries(transformed).filter(([_, value]) => value !== '')) as Record<string, string | number | boolean>;
}
export const cleanPayload = <T extends object,>(payload: T, clearEmpty: boolean = false): Partial<T> => {
  return Object.fromEntries(Object.entries(payload).filter(([_, value]) => {
    if (!clearEmpty) return value !== '' && value !== null && value !== undefined;
    if (value === '' || value === null || value === undefined) return false;
    if (Array.isArray(value) && value.length === 0) return false;
    if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return false;
    return true;
  })) as Partial<T>;
};
export const pickFirstError = (error: string | string[]) => {
  if (Array.isArray(error)) {
    return error[0];
  } else return error;
};
export function formatToUSD(value: string | number): string {
  const numberValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numberValue)) {
    return '';
  }
  return numberValue.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD'
  });
}
export const convertDateToYYYYMMDD = (dateString: string | null): string | null => {
  if (!dateString) return null;
  const date = dateUtils.parse.safe(dateString);
  if (!date || isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
};
export const toSafeString = (value: string | number | boolean | object | undefined | null): string => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'object') {
    // Handle objects safely - could be arrays or plain objects
    try {
      return JSON.stringify(value);
    } catch {
      return '';
    }
  }
  return String(value);
};
export const toSafeNumber = (value: string | number | undefined | null): number => {
  if (value === undefined || value === null) return 0;
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};
export const toSafeBoolean = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value === 'Y' || value === 'true' || value === '1';
  if (typeof value === 'number') return value === 1;
  return false;
};
export const calculateBaseRate = (baseRatePercent: number, listPrice: number) => {
  const baseRate = Number((baseRatePercent * listPrice).toFixed(3));
  const baseRatePercentage = Number((baseRatePercent * 100).toFixed(3));
  return {
    baseRate,
    baseRatePercentage
  };
};
export const calculatePriceFloor = (percentList: number, percentBase: number, listPrice: number, baseRate: number, setPriceFloorPercentageName: (value: string) => void) => {
  if (percentList === 0 || percentList === null) {
    // Use base rate calculation
    const priceFloorPercentage = percentBase * 100;
    const priceFloor = percentBase * baseRate;
    setPriceFloorPercentageName('% Base');
    return {
      priceFloor,
      priceFloorPercentage
    };
  } else {
    // Use list price calculation
    const priceFloorPercentage = percentList * 100;
    const priceFloor = listPrice * percentList;
    setPriceFloorPercentageName('% List');
    return {
      priceFloor,
      priceFloorPercentage
    };
  }
};
export function createOptionLabelGetter(primaryKey: string, item: unknown): string {
  if (typeof item === 'object' && item !== null) {
    const obj = item as Record<string, unknown>;
    if (primaryKey in obj && typeof obj[primaryKey] === 'string') {
      return obj[primaryKey];
    }
    if ('label' in obj && typeof obj['label'] === 'string') {
      return obj['label'];
    }
  }
  return '';
}
export const getDairyCustomerTextColor = (customer: CustomerNameListData | null): string => {
  if (!customer) return 'black';
  const creditFlag = customer.cuCustomerCreditFlag;
  const closedFlag = customer.cuCustomerClosedFlag;
  if (closedFlag === 'Y') {
    return 'red';
  } else if (['D', 'SH', 'CL'].includes(creditFlag)) {
    return 'blue';
  }
  return 'black';
};
export const getHistoryByModelTextColor = (customer: HistoryByModelCustomerNameListData | HistoryByModelCustomerNumberListData | null): string => {
  if (!customer) return 'black';
  const creditFlag = customer.CREDITFLAG;
  const closedFlag = customer.CLOSEDFLAG;
  if (closedFlag === 'Y') {
    return 'red';
  } else if (['D', 'SH', 'CL'].includes(creditFlag)) {
    return 'blue';
  }
  return 'black';
};
export const getHeaderSortField = {
  roLastChangeDate: 'RO_LAST_CHANGE_DATE',
  adAssetDescNumber: 'AD_ASSET_DESC_NUMBER',
  ammMfgMdlNum: 'AMM_MFG_MDL_NUM',
  roDateIssued: 'RO_DATE_ISSUED',
  rcRepairCode: 'RC_REPAIR_CODE',
  empName: 'EMP_NAME',
  ecCode: 'EC_CODE',
  adPhysicalLoc: 'AD_PHYSICAL_LOC',
  adLocationType: 'AD_LOCATION_TYPE',
  adLoc: 'AD_LOC',
  adWarrantyExpiration: 'AD_WARRANTY_EXPIRATION',
  prhNumber: 'PRH_NUMBER',
  adExpedite: 'AD_EXPEDITE',
  adRepairReturnFlag: 'AD_REPAIR_RETURN_FLAG',
  roRepairOrderNum: 'RO_REPAIR_ORDER_NUM'
};
export const getHeaderSortFieldRepairCodes = {
  rcRepairCode: 'REPAIRCODEASC',
  rcDesc: 'REPAIRASC'
};
export const getHeaderSortFieldLabStandards = {
  adAssetDescNum: 'AD_ASSET_DESC_NUM',
  ammMfgMdlNum: 'AMM_MFG_MDL_NUM'
};
export const getHeaderSortFieldLabToVendor = {
  lastChangeDate: 'RO_LAST_CHANGE_DATE',
  assetId: 'AD_ASSET_DESC_NUMBER',
  mfgMdl: 'AMM_MFG_MDL_NUM',
  eqpCode: 'EC_CODE',
  dateIssued: 'RO_DATE_ISSUED',
  empName: 'EMP_NAME',
  rmaNum: 'RO_RMA_NUM',
  vendorSite: 'VENDOR_SITE',
  oscarNum: 'OSCAR_NUM',
  repairCode: 'RC_REPAIR_CODE',
  expedite: 'AD_EXPEDITE',
  repairReturn: 'AD_REPAIR_RETURN_FLAG',
  repairOrder: 'RO_REPAIR_ORDER_NUM',
  assetOnRa: 'ASSET_ON_RA'
};
export const getHeaderSortFieldLabFromVendor = {
  roLastChangeDate: 'RO_LAST_CHANGE_DATE',
  adAssetDescNumber: 'AD_ASSET_DESC_NUMBER',
  ammMfgMdlNum: 'AMM_MFG_MDL_NUM',
  ecCode: 'EC_CODE',
  roDateIssued: 'RO_DATE_ISSUED',
  rcRepairCode: 'RC_REPAIR_CODE',
  empName: 'EMP_NAME',
  roOscarNum: 'RO_OSCAR_NUM',
  vendorNum: 'VENDOR_NUM',
  vendorName: 'VENDOR_NAME',
  roToVendor: 'RO_TO_VENDOR',
  daysAtVendor: 'DAYS_AT_VENDOR',
  adExpedite: 'AD_EXPEDITE',
  adRepairReturnFlag: 'AD_REPAIR_RETURN_FLAG',
  prhNumber: 'PRH_NUMBER',
  roRepairOrderNum: 'RO_REPAIR_ORDER_NUM'
};
export const getHeaderSortFieldLabAtVendor = {
  roLastChangeDate: 'RO_LAST_CHANGE_DATE',
  adAssetDescNumber: 'AD_ASSET_DESC_NUMBER',
  ammMfgMdlNum: 'AMM_MFG_MDL_NUM',
  ecCode: 'EC_CODE',
  roToVendor: 'TO_VENDOR',
  roEtaFromVenDate: 'ETA',
  roRmaNum: 'RMA',
  vendorName: 'VENDOR_NAME',
  vendorNum: 'VENDOR_NO',
  roOscarNum: 'OSCAR_NO',
  daysAtVendor: 'DAV',
  empName: 'VENDOR_NAME',
  adExpedite: 'EXP',
  adRepairReturnFlag: 'RR',
  roDateIssued: 'RO_DATE_ISSUED',
  rcRepairCode: 'RC_REPAIR_CODE',
  roRepairOrderNum: 'RO_REPAIR_ORDER_NUM',
  vaSiteNum: 'VENDOR',
  assetOnRa: 'ASSET_ON',
  roDamageN: 'DAMAGE'
};
export const getSingleValidationMessage = (formErrors: {
  [key: string]: string;
}) => {
  const firstErrorKey = Object.keys(formErrors)[0];
  const firstErrorValue = Object.values(formErrors)[0];
  const newErrorObject = {
    [firstErrorKey]: firstErrorValue
  };
  return newErrorObject;
};